import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ChevronDown } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { API_BASE_URL } from '../config/apiConfig';

const AdminDashboard = () => {
  const { user } = useAppContext();
  const { addToast } = useToast();

  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    pickupAddress: '',
    destinationCountry: '',
    destinationAddress: '',
    weight: '',
    status: 'Pickup Requested',
    currentLoc: '',
    description: '',
  });

  const destinationCountries = [
    'United States',
    'Canada',
    'United Kingdom',
    'Germany',
    'France',
    'Nigeria',
    'Other',
  ];

  const statusOptions = [
    'Pickup Requested',
    'In Transit',
    'Customs Clearance',
    'Out for Delivery',
    'Delivered',
  ];

  // ✅ FIXED: Format date properly
// Or wherever your admin shipment list is

const formatDate = (date) => {
  if (!date) return 'N/A';

  try {
    let dateObj;

    // ✅ Handle Firestore Timestamp { _seconds, _nanoseconds }
    if (date && typeof date === 'object' && '_seconds' in date) {
      dateObj = new Date(date._seconds * 1000);
    }
    // ✅ Handle Firestore Timestamp { seconds, nanoseconds }
    else if (date && typeof date === 'object' && 'seconds' in date) {
      dateObj = new Date(date.seconds * 1000);
    }
    // ✅ Handle toDate() method
    else if (date && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    }
    // ✅ Handle string
    else if (typeof date === 'string') {
      dateObj = new Date(date);
    }
    // ✅ Handle number (milliseconds)
    else if (typeof date === 'number') {
      dateObj = new Date(date);
    }
    // ✅ Handle Date object
    else if (date instanceof Date) {
      dateObj = date;
    }
    else {
      return 'N/A';
    }

    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('❌ Error formatting date:', error);
    return 'N/A';
  }
};

  // ✅ Fetch all shipments
  const fetchShipments = async () => {
    setIsLoading(true);
    try {
      const token = await user.getIdToken();
      console.log('📋 Fetching shipments with token...');

      const response = await fetch(`${API_BASE_URL}/shipments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch shipments: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Shipments fetched:', data);
      
      // ✅ Sort by createdAt
      let sorted = [...data];
      if (sortOrder === 'newest') {
        sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });
      } else {
        sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateA - dateB;
        });
      }
      
      setShipments(sorted);
      addToast('✅ Shipments loaded', 'success');
    } catch (error) {
      console.error('❌ Error fetching shipments:', error);
      addToast(`❌ Error loading shipments: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, [sortOrder]);

  // ✅ Add/Update shipment
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customerName || !formData.email || !formData.pickupAddress || !formData.destinationCountry || !formData.destinationAddress) {
      addToast('⚠️ Please fill all required fields', 'error');
      return;
    }

    try {
      const token = await user.getIdToken();

      if (editingId) {
        // Update existing shipment
        console.log('✏️ Updating shipment:', editingId);
        console.log('📝 Form data:', formData);
        
        const response = await fetch(`${API_BASE_URL}/shipments/${editingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerName: formData.customerName,
            email: formData.email,
            phone: formData.phone,
            pickupAddress: formData.pickupAddress,
            destinationCountry: formData.destinationCountry,
            destinationAddress: formData.destinationAddress,
            weight: formData.weight,
            status: formData.status,
            currentLoc: formData.currentLoc,
            description: formData.description,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ Server error:', errorData);
          throw new Error(errorData.message || 'Failed to update shipment');
        }
        
        const result = await response.json();
        console.log('✅ Update response:', result);
        addToast('✅ Shipment updated successfully', 'success');
      } else {
        // Create new shipment (manual entry)
        console.log('📦 Creating new shipment...');
        const response = await fetch(`${API_BASE_URL}/shipments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerName: formData.customerName,
            email: formData.email,
            phone: formData.phone,
            pickupAddress: formData.pickupAddress,
            destinationCountry: formData.destinationCountry,
            destinationAddress: formData.destinationAddress,
            weight: formData.weight,
            description: formData.description,
            pickupDate: new Date().toISOString().split('T')[0],
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create shipment');
        }
        
        addToast('✅ Shipment created successfully', 'success');
      }

      setShowModal(false);
      setEditingId(null);
      setFormData({
        customerName: '',
        email: '',
        phone: '',
        pickupAddress: '',
        destinationCountry: '',
        destinationAddress: '',
        weight: '',
        status: 'Pickup Requested',
        currentLoc: '',
        description: '',
      });
      fetchShipments();
    } catch (error) {
      console.error('❌ Error saving shipment:', error);
      addToast(`❌ ${error.message}`, 'error');
    }
  };

  // ✅ Delete shipment
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this shipment?')) return;

    try {
      const token = await user.getIdToken();
      console.log('🗑️ Deleting shipment:', docId);

      const response = await fetch(`${API_BASE_URL}/shipments/${docId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to delete shipment');
      addToast('✅ Shipment deleted successfully', 'success');
      fetchShipments();
    } catch (error) {
      console.error('❌ Error deleting shipment:', error);
      addToast(`❌ ${error.message}`, 'error');
    }
  };

  // ✅ Edit shipment
  const handleEdit = (shipment) => {
    setFormData({
      customerName: shipment.customerName,
      email: shipment.email,
      phone: shipment.phone,
      pickupAddress: shipment.pickupAddress,
      destinationCountry: shipment.destinationCountry,
      destinationAddress: shipment.destinationAddress,
      weight: shipment.weight || '',
      status: shipment.status,
      currentLoc: shipment.currentLoc,
      description: shipment.description || '',
    });
    setEditingId(shipment.docId);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">📊 Admin Dashboard</h1>
            <p className="text-slate-300">Welcome, {user?.email}</p>
          </div>
          {/* 2. This div will hold both buttons */}
          <div className="flex items-center gap-4">
            {/* 3. ADD THIS COMPLETE LINK COMPONENT */}
            <Link 
              to="/admin/manage-users"
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center gap-2"
            >
              Manage Users
            </Link>
          <button
            onClick={() => {
              setEditingId(null);
              setFormData({
                customerName: '',
                email: '',
                phone: '',
                pickupAddress: '',
                destinationCountry: '',
                destinationAddress: '',
                weight: '',
                status: 'Pickup Requested',
                currentLoc: '',
                description: '',
              });
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 cursor-pointer transition"
          >
            <Plus className="h-5 w-5" /> Add Shipment
          </button>
        </div>
      </div>
        {/* Sorting Filter */}
        <div className="mb-6 flex gap-4">
          <div className="flex items-center gap-2">
            <label className="text-white font-semibold">Sort by:</label>
            <div className="relative">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 appearance-none bg-white text-slate-900 font-semibold cursor-pointer"
              >
                <option value="newest">🆕 Newest First</option>
                <option value="oldest">🕐 Oldest First</option>
              </select>
              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Shipments Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading shipments...</p>
          </div>
        ) : shipments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p className="text-gray-600 text-lg">No shipments found. Create one to get started!</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-2xl overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Tracking ID</th>
                  <th className="px-6 py-3 text-left">Customer</th>
                  <th className="px-6 py-3 text-left">Email</th>
                  <th className="px-6 py-3 text-left">Destination</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Pickup Date</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  <th className="px-6 py-3 text-left">Date</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((shipment, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50 transition">
                    <td className="px-6 py-4 font-semibold text-slate-900">{shipment.id}</td>
                    <td className="px-6 py-4">{shipment.customerName}</td>
                    <td className="px-6 py-4 text-sm">{shipment.email}</td>
                    <td className="px-6 py-4 text-sm">{shipment.destinationCountry}</td>
                     <td className="px-6 py-4 text-sm">
                      {shipment.createdAt?.toDate ? new Date(shipment.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {shipment.eta ? new Date(shipment.eta).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        shipment.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                        shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                        shipment.status === 'Out for Delivery' ? 'bg-cyan-100 text-cyan-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {shipment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold">{formatDate(shipment.createdAt)}</td>
                    <td className="px-6 py-4 flex gap-2">
                      <button
                        onClick={() => handleEdit(shipment)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded-lg transition cursor-pointer"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(shipment.docId)}
                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal for Add/Edit */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full max-h-screen overflow-y-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {editingId ? '✏️ Edit Shipment' : '📦 Add New Shipment'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Customer Name *</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={formData.customerName}
                    onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Email *</label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Phone</label>
                    <input
                      type="tel"
                      placeholder="+1 234 567 8900"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Pickup Address *</label>
                  <input
                    type="text"
                    placeholder="123 Main Street, New York, NY 10001"
                    value={formData.pickupAddress}
                    onChange={(e) => setFormData({...formData, pickupAddress: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Destination Country *</label>
                    <select
                      value={formData.destinationCountry}
                      onChange={(e) => setFormData({...formData, destinationCountry: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      required
                    >
                      <option value="">-- Select --</option>
                      {destinationCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Destination Address *</label>
                    <input
                      type="text"
                      placeholder="456 Oak Avenue, London, UK"
                      value={formData.destinationAddress}
                      onChange={(e) => setFormData({...formData, destinationAddress: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Weight (kg)</label>
                    <input
                      type="number"
                      placeholder="5.5"
                      step="0.1"
                      value={formData.weight}
                      onChange={(e) => setFormData({...formData, weight: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Current Location</label>
                  <input
                    type="text"
                    placeholder="New York, NY"
                    value={formData.currentLoc}
                    onChange={(e) => setFormData({...formData, currentLoc: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Description</label>
                  <textarea
                    placeholder="Package description..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 h-20"
                  />
                </div>

                <div className="flex gap-4 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition cursor-pointer"
                  >
                    {editingId ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-slate-900 font-bold py-2 px-4 rounded-lg transition cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;